/**
 * CleanCodeModal
 *
 * Shows a Monaco DiffEditor (before/after) of AI Clean Code results.
 * User reviews the diff and clicks Apply to commit, or Cancel to discard.
 * Never auto-applies — explicit user action required.
 */

import { useState } from 'react'
import { X, Wand2, Copy, Check } from 'lucide-react'
import { DiffEditor } from '@monaco-editor/react'
import './CleanCodeModal.css'

const DEBUG_CLEAN = false

function CleanCodeModal({ original, cleaned, filename, language = 'java', onApply, onClose }) {
  const [copied, setCopied] = useState(false)

  const originalLines = original.split('\n').length
  const cleanedLines = cleaned.split('\n').length
  const lineDelta = cleanedLines - originalLines
  const changeLabel =
    lineDelta === 0
      ? 'Reformatted — same line count'
      : lineDelta > 0
        ? `${lineDelta} line${lineDelta !== 1 ? 's' : ''} added`
        : `${Math.abs(lineDelta)} line${Math.abs(lineDelta) !== 1 ? 's' : ''} removed`

  if (DEBUG_CLEAN) {
    console.log('[CleanCode] Modal open', { filename, language, originalLines, cleanedLines, lineDelta })
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(cleaned)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="cc-overlay" onClick={onClose}>
      <div className="cc-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="cc-header">
          <div className="cc-title">
            <Wand2 size={16} />
            <span>AI Clean Code</span>
            <span className="cc-filename">{filename}</span>
          </div>
          <button className="cc-close" onClick={onClose} title="Cancel — no changes applied">
            <X size={16} />
          </button>
        </div>

        {/* Summary bar */}
        <div className="cc-summary">
          {changeLabel} — review the diff below, then click <strong>Apply</strong> to update the editor, or <strong>Cancel</strong> to discard.
        </div>

        {/* Monaco diff view — original (left) vs cleaned (right) */}
        <div className="cc-diff-wrapper">
          <DiffEditor
            original={original}
            modified={cleaned}
            language={language}
            theme="vs-dark"
            options={{
              readOnly: true,
              renderSideBySide: true,
              minimap: { enabled: false },
              fontSize: 12,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              padding: { top: 8, bottom: 8 },
              renderIndicators: true,
              ignoreTrimWhitespace: false,
            }}
          />
        </div>

        {/* Footer */}
        <div className="cc-footer">
          <button className="cc-btn secondary" onClick={handleCopy} title="Copy cleaned code to clipboard">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy Cleaned'}
          </button>

          <div className="cc-footer-right">
            <button className="cc-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="cc-btn primary" onClick={() => onApply(cleaned)}>
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CleanCodeModal
